import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { api, unwrap, type TeamSummary } from "../api/client";
import { useUsers } from "./UsersPage";

const TEAMS_KEY = ["teams"] as const;

export function TeamsPage() {
  const qc = useQueryClient();
  const teams = useQuery({ queryKey: TEAMS_KEY, queryFn: () => unwrap(api.GET("/api/teams")) });
  const [selected, setSelected] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const create = useMutation({
    mutationFn: () => unwrap(api.POST("/api/teams", { body: { name, description } })),
    onSuccess: (t) => {
      setName("");
      setDescription("");
      setSelected(t.id);
      void qc.invalidateQueries({ queryKey: TEAMS_KEY });
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };

  const team = teams.data?.find((t) => t.id === selected);

  return (
    <Stack spacing={3} component="section">
      <Typography variant="h5" component="h2">
        Teams
      </Typography>
      <Typography color="text.secondary">
        Each team owns a team library. Recordings from the team's sources are imported into it.
      </Typography>
      <Paper component="form" variant="outlined" onSubmit={submit} sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="h6" component="h3">
            New team
          </Typography>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
            <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} />
          </Stack>
          {create.isError && <Alert severity="error">{create.error.message}</Alert>}
          <div>
            <Button type="submit" variant="contained" disabled={create.isPending}>
              Create team
            </Button>
          </div>
        </Stack>
      </Paper>

      {teams.isError && <Alert severity="error">{teams.error.message}</Alert>}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Team</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Members</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {teams.data?.map((t) => (
              <TableRow key={t.id} selected={t.id === selected}>
                <TableCell>{t.name}</TableCell>
                <TableCell>{t.description}</TableCell>
                <TableCell>{t.member_count}</TableCell>
                <TableCell>
                  <Button onClick={() => setSelected(t.id)}>Members</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {team && <TeamMembers team={team} />}
    </Stack>
  );
}

function TeamMembers({ team }: { team: TeamSummary }) {
  const qc = useQueryClient();
  const key = ["teams", team.id, "members"];
  const members = useQuery({
    queryKey: key,
    queryFn: () => unwrap(api.GET("/api/teams/{id}/members", { params: { path: { id: team.id } } })),
  });
  const users = useUsers();
  const [userId, setUserId] = useState("");
  const [isLead, setIsLead] = useState(false);

  const add = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/teams/{id}/members", {
          params: { path: { id: team.id } },
          body: { user_id: userId, is_lead: isLead },
        }),
      ),
    onSuccess: () => {
      setUserId("");
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: TEAMS_KEY });
    },
  });

  const candidates = users.data?.filter((u) => !members.data?.some((m) => m.user_id === u.id));

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" component="h3">
        {team.name}: members
      </Typography>
      <List dense>
        {members.data?.map((m) => (
          <ListItem key={m.user_id}>
            <ListItemText primary={m.display_name} secondary={`${m.username}${m.is_lead ? " · team lead" : ""}`} />
          </ListItem>
        ))}
        {members.data?.length === 0 && (
          <ListItem>
            <ListItemText secondary="No members yet." />
          </ListItem>
        )}
      </List>
      <Stack
        component="form"
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ alignItems: { sm: "center" } }}
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate();
        }}
      >
        <FormControl sx={{ minWidth: 260 }} size="small">
          <InputLabel id="add-member-label">Add a user</InputLabel>
          <Select labelId="add-member-label" label="Add a user" value={userId} onChange={(e) => setUserId(e.target.value)}>
            {candidates?.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.display_name} ({u.username})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel control={<Checkbox checked={isLead} onChange={(e) => setIsLead(e.target.checked)} />} label="Team lead" />
        <Button type="submit" variant="contained" disabled={!userId || add.isPending}>
          Add
        </Button>
      </Stack>
      {add.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {add.error.message}
        </Alert>
      )}
    </Paper>
  );
}
